/**
 * exportBrandedPdf — generic branded PDF export using jsPDF + jspdf-autotable.
 * Call with a table config and it handles header, footer, and autotable.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addBrandedHeader, addBrandedFooter } from "./jsPdfBranding";
import moment from "moment";

/**
 * @param {Object} opts
 * @param {string} opts.facilityName
 * @param {string} opts.reportTitle      — e.g. "Medication List"
 * @param {string} opts.reportSubtitle   — e.g. "John Doe — 03/26/2026"
 * @param {string[]} opts.columns        — header labels
 * @param {string[][]} opts.rows         — data rows
 * @param {string} [opts.filename]       — download filename
 * @param {string} [opts.orientation]    — "l" | "p" (default "l")
 * @param {Object[]} [opts.metaRows]     — optional [{label, value}] shown above the table
 */
const exportBrandedPdf = (opts = {}) => {
  const {
    facilityName = "Facility",
    reportTitle = "Report",
    reportSubtitle = "",
    columns = [],
    rows = [],
    filename = `report_${moment().format("YYYY-MM-DD")}.pdf`,
    orientation = "l",
    metaRows = [],
  } = opts;

  const doc = new jsPDF(orientation, "pt", "a4");

  const contentStartY = addBrandedHeader(doc, {
    facilityName,
    reportTitle,
    reportSubtitle,
    generatedDate: moment().format("MM/DD/YYYY"),
  });

  let startY = contentStartY;

  // Optional meta rows (label: value pairs)
  if (metaRows.length > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor("#777777");
    metaRows.forEach((meta, i) => {
      doc.text(`${meta.label}: ${meta.value}`, 30, startY + i * 12);
    });
    startY += metaRows.length * 12 + 6;
  }

  autoTable(doc, {
    head: [columns],
    body: rows,
    startY,
    styles: { fontSize: 8, cellPadding: 4, font: "helvetica" },
    headStyles: {
      fillColor: [29, 41, 57],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
      halign: "left",
    },
    alternateRowStyles: { fillColor: [248, 248, 246] },
    margin: { left: 30, right: 30 },
  });

  addBrandedFooter(doc);
  doc.save(filename);
};

export default exportBrandedPdf;
